import { useMemo } from "react"
import { useIntl } from "react-intl"

import { AssignedConversation } from "@doktor-se/bones-ui/dist/web-shared/types"
import { formatPnr } from "@doktor-se/bones-ui/dist/web-shared/utils"

import { BooleanCell, DateTimePillCell, NameCell } from "components/v2/conversationList/Cells"
import ConversationTable, { ConversationListItem } from "components/v2/conversationList/ConversationTable"
import { createSortValues, sortValueFunctions } from "components/v2/conversationList/sortUtils"

interface PausedConversationListProps {
  conversations: AssignedConversation[]
}

const PausedConversationList = ({ conversations }: PausedConversationListProps) => {
  const intl = useIntl()
  const columns = [
    {
      key: "pnr",
      name: intl.formatMessage({ id: "label.overview.pnr" })
    },
    {
      key: "name",
      name: intl.formatMessage({ id: "label.overview.name" })
    },
    {
      key: "start",
      name: intl.formatMessage({ id: "label.overview.errand_started" })
    },
    {
      key: "listed",
      name: intl.formatMessage({ id: "label.overview.listed" })
    },
    {
      key: "paused",
      name: intl.formatMessage({ id: "label.overview.paused" })
    },
    {
      key: "recordConsent",
      name: intl.formatMessage({ id: "label.overview.medical_consent" })
    },
    {
      key: "medicationConsent",
      name: intl.formatMessage({ id: "label.overview.medication_consent" })
    }
  ]

  const list = useMemo(
    () =>
      conversations.map(conv => {
        const isChildConversation = conv.profile?.type === "child"
        return {
          conversation: conv,
          values: {
            pnr: isChildConversation
              ? formatPnr(conv.profile!.data!.securityNumber, conv.metadata?.region)
              : formatPnr(conv.account?.pnr, conv.metadata?.region),
            name: (
              <NameCell
                name={isChildConversation ? conv.profile!.name! : conv.account.displayName!}
                isChild={isChildConversation}
              />
            ),
            start: <DateTimePillCell value={conv.created} />,
            listed: <BooleanCell value={!!conv.customerMetadata?.listedAtClinicName} />,
            paused: <BooleanCell value={!!conv?.snoozedUntil} />,
            recordConsent: <BooleanCell value={!!conv.customerMetadata?.otherJournal} />,
            medicationConsent: <BooleanCell value={!!conv.customerMetadata?.medicationList} />
          },
          sortValues: {
            ...createSortValues(conv, ["pnr", "name", "listed", "recordConsent", "medicationConsent"], {}),
            start: sortValueFunctions.created(conv),
            paused: sortValueFunctions.paused(conv)
          }
        } as ConversationListItem<AssignedConversation>
      }),
    [conversations]
  )

  return (
    <ConversationTable columns={columns} items={list} emptyMessage={intl.formatMessage({ id: "label.paused.empty" })} />
  )
}

export default PausedConversationList
